import { toast } from 'sonner';

/**
 * FRONT-1019: the single, shared "moved to Recycle bin → Undo" toast used by
 * every delete-to-Recycle-bin surface (Files, Documents, Memory/graph entities,
 * Work projects, Chats, the Inbox briefing feed).
 *
 * Before this helper the same toast+Undo block was hand-copied across Memory
 * and Work, and Files/Chats/Documents/Inbox had no Undo at all. Routing every
 * surface through `showUndoToast` gives one affordance and one wording:
 *
 *   - the confirmation reads "Moved to Recycle bin" (single) or
 *     "Moved N <things> to Recycle bin" (bulk),
 *   - an "Undo" action button that restores the item(s),
 *   - a "Restored" success toast, or a plain-language failure toast that points
 *     the user at the Recycle bin as the longer-lived fallback.
 *
 * The Recycle bin's own Restore stays the durable fallback; this is the
 * immediate, one-tap post-action Undo. "Recycle bin" is the app-wide term for
 * this destination — no surface says "Trash" (FRONT-1019).
 */

const UNDO_TOAST_DURATION_MS = 8000;

/**
 * Build the consistent "moved to Recycle bin" confirmation message.
 * Single item → "Moved to Recycle bin" (the item the user just acted on is obvious).
 * Multiple    → "Moved N <plural> to Recycle bin", e.g. "Moved 3 files to Recycle bin".
 */
export function trashedMessage(count = 1, noun?: string, nounPlural?: string): string {
  if (count <= 1) return 'Moved to Recycle bin';
  const plural = nounPlural ?? (noun ? `${noun}s` : 'items');
  return `Moved ${count} ${plural} to Recycle bin`;
}

export interface UndoToastOptions {
  /** How many items were moved to the Recycle bin — drives singular/plural wording. Default 1. */
  count?: number;
  /** Singular noun for the item, e.g. "file". Only appears in the bulk (count > 1) wording. */
  noun?: string;
  /** Explicit plural when it isn't just `${noun}s`, e.g. "entities", "people". */
  nounPlural?: string;
  /**
   * Runs when the user taps Undo. Awaited: on success we show "Restored", on
   * failure a plain-language error. Omit to show a Recycle bin confirmation with
   * no Undo action (e.g. when nothing can be restored).
   */
  onUndo?: () => void | Promise<unknown>;
  /**
   * FRONT-3145: replaces the "Moved to Recycle bin" wording for a surface that
   * has no Recycle bin of its own (the Inbox, whose deleted items come back
   * through its "Show deleted items" switch). Omit everywhere else.
   */
  message?: string;
  /** Second line under the message, e.g. where to find the item again. */
  description?: string;
}

/**
 * Show the shared "Moved to Recycle bin" toast with a one-tap Undo. The caller
 * keeps hold of the ids/filenames/session-ids it just moved and supplies
 * `onUndo` to restore exactly those — no server round-trip is needed to learn
 * what to put back.
 */
export function showUndoToast(opts: UndoToastOptions = {}): void {
  const { count = 1, noun, nounPlural, onUndo, message, description } = opts;

  // Named so the async undo → "Restored" / error flow reads as one step. If
  // undo fails the item is still safe in the Recycle bin, so the failure
  // message stays simple and doesn't send the user chasing it.
  async function runUndo(): Promise<void> {
    try {
      await onUndo?.();
      toast.success('Restored');
    } catch {
      toast.error("Couldn't undo — please try again.");
    }
  }

  toast.success(message ?? trashedMessage(count, noun, nounPlural), {
    ...(description ? { description } : {}),
    duration: UNDO_TOAST_DURATION_MS,
    action: onUndo ? { label: 'Undo', onClick: runUndo } : undefined,
  });
}
